import { Link } from 'react-router-dom';
import Notice from '../../components/Notice.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import Toggle from '../../components/Toggle.jsx';
import { customerSectionPath } from '../../lib/navigation.js';
import { COLLECTOR_CATEGORIES, ALL_COLLECTOR_IDS, licenseRequirement } from '../../../../shared/collectors.mjs';
import { permissionsForSelection, PERMISSION_JUSTIFICATIONS } from '../../../../shared/permission-map.mjs';
import { ESSENTIAL_SOURCES } from '../../../../shared/permission-presets.mjs';
import PermissionConsentTable from '../../components/PermissionConsentTable.jsx';
import { selectionFor } from './use-collection-settings.js';

// Sources (Data collection, mockup 3): one block per category in a 2-column grid, a switch per source with its license
// note inline, the permissions the selection needs, and a Save bar that sticks to the bottom of the screen while the
// selection has unsaved changes. Status color lives only in dots; every note is ink or muted text.

export const SOURCES_ID = 'sources';

// The shared catalog titles are Title Case with "&"; the page says them in sentence case with "and" (ENTRAID-347 design
// language). An unknown category falls back to its catalog title with "and".
const CATEGORY_TITLE = Object.freeze({
  identity: 'Identity and directory',
  access: 'Access policies',
  apps: 'Apps and permissions',
  audit: 'Audit and sign-in logs',
  security: 'Security and compliance',
  auth: 'Authentication and MFA',
  licensing: 'Licensing',
  m365: 'Microsoft 365 and SharePoint',
  posture: 'Tenant settings',
});
export const categoryTitle = (cat) => CATEGORY_TITLE[cat.id] ?? String(cat.title).replace(/ & /g, ' and ');

function Dot({ color }) {
  return <span aria-hidden="true" className="mt-[7px] inline-block h-2 w-2 shrink-0 rounded-sm" style={{ background: color }} />;
}

/**
 * The new-sources notice (ENTRAID-312) at the top of the page: what is new, what needs a license, and "Enable all"
 * for the ones that need none. Saving stays the admin's step.
 */
export function NewSourcesNotice({ form }) {
  const { newSources, newFree, newLicensed, freePending, enableAllNew } = form;
  if (newSources.length === 0) return null;
  const action = newFree.length > 0
    ? (freePending.length > 0
      ? <button type="button" className="btn-primary text-sm whitespace-nowrap" onClick={enableAllNew}>Enable all</button>
      : <span className="text-[13px] text-ipc-muted whitespace-nowrap">Enabled. Press Save changes to apply.</span>)
    : <a className="btn-secondary text-sm whitespace-nowrap" href={`#${SOURCES_ID}`}>Review below</a>;
  return (
    <Notice
      tone="info"
      testId="new-sources-notice"
      title={newSources.length === 1 ? '1 new data source is available since you last saved.' : `${newSources.length} new data sources are available since you last saved.`}
      action={action}
    >
      {newFree.length > 0 && <>New: {newFree.map((s) => s.label).join(', ')}. </>}
      {newLicensed.length > 0 && (
        <>Needs a license: {newLicensed.map((s) => `${s.label} (${s.requires.replace(/^Requires /, '')})`).join(', ')}. Turn these on yourself if your tenant has the license.</>
      )}
    </Notice>
  );
}

/**
 * "Permissions you will grant" (ENTRAID-350 spec 7.1): the Microsoft Graph application permissions the selected sources
 * need, each with its justification. Nothing is asked of Microsoft until Save. Core's User.Read.All is always included.
 */
export function PermissionsPanel({ enabled }) {
  const selection = selectionFor(enabled);
  const permissions = permissionsForSelection(selection);
  return (
    <details className="mt-4 border-t border-ipc-border pt-3" data-testid="permissions-panel">
      <summary className="cursor-pointer text-sm font-semibold text-ipc-ink">
        Permissions you will grant ({permissions.length} of {Object.keys(PERMISSION_JUSTIFICATIONS).length})
      </summary>
      <p className="mt-2 text-[13px] text-ipc-muted">
        {selection.mode === 'all'
          ? 'All data sources are on, so your Microsoft Entra app asks for every read-only permission below.'
          : 'Your Microsoft Entra app asks only for what the selected sources need. User.Read.All is always included.'}
      </p>
      <div className="mt-2">
        <PermissionConsentTable permissions={permissions} caption={`Your Microsoft Entra app asks for ${permissions.length} permission${permissions.length === 1 ? '' : 's'}.`} />
      </div>
    </details>
  );
}

function SourceRow({ c, form, isNew, referenceHref, waitsForConsent }) {
  const { enabled, toggle, loaded, hints, licenseDisabled } = form;
  const license = licenseRequirement(c.id);
  return (
    <li className="border-t border-ipc-border py-2.5 first:border-t-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <span className="text-sm text-ipc-ink">{c.label}</span>
          {isNew ? (
            <span className="ml-2 rounded border border-ipc-border px-1.5 text-[11.5px] font-semibold text-ipc-primary" data-testid={`new-${c.id}`}>New</span>
          ) : null}
          {licenseDisabled[c.id] ? (
            <span className="mt-0.5 flex gap-2 text-[13px] leading-snug text-ipc-ink" data-testid={`license-${c.id}`}>
              <Dot color="var(--warn-dot)" />
              <span>
                <span className="font-semibold">{licenseDisabled[c.id].reason ?? license}.</span>{' '}
                Turned off automatically because your tenant does not have this license. Turn it on to try anyway.
              </span>
            </span>
          ) : license ? (
            <span className="mt-0.5 block text-[13px] text-ipc-muted" data-license-note="">{license.replace(/^Requires /, 'Needs ')}</span>
          ) : null}
          {waitsForConsent ? (
            <span className="mt-0.5 flex gap-2 text-[13px] leading-snug text-ipc-ink" data-testid={`needs-consent-${c.id}`}>
              <Dot color="var(--warn-dot)" />
              <span><span className="font-semibold">Needs admin consent.</span> Collected once a Global Administrator approves the new permission.</span>
            </span>
          ) : null}
          {c.note ? <span className="mt-0.5 block text-[13px] text-ipc-muted">{c.note}</span> : null}
          {hints[c.id] ? (
            <span className="mt-0.5 block text-[13px] leading-snug text-ipc-muted [overflow-wrap:anywhere]" data-testid={`hint-${c.id}`}>
              <code className="font-mono">{hints[c.id].path}</code>
              {' '}&middot; {hints[c.id].cadence}.{' '}
              <Link to={referenceHref(c.id)} className="font-semibold text-ipc-light hover:underline">Data reference</Link>
            </span>
          ) : null}
        </div>
        <Toggle checked={enabled.has(c.id)} onChange={() => toggle(c.id)} ariaLabel={c.label} disabled={!loaded} showState={false} />
      </div>
    </li>
  );
}

function Category({ cat, form, newIds, referenceHref, needsConsent }) {
  const ids = cat.collectors.map((c) => c.id);
  const on = ids.filter((id) => form.enabled.has(id)).length;
  const allOn = on === ids.length && ids.length > 0;
  return (
    <div className="min-w-0 rounded-xl border border-ipc-border px-4 py-3" data-category={cat.id}>
      <div className="flex items-baseline gap-2">
        <h3 className="min-w-0 flex-1">{categoryTitle(cat)}</h3>
        <span className="text-[13px] text-ipc-muted">{on} of {ids.length}</span>
        <button type="button" onClick={() => form.toggleCategory(cat)} className="tap text-[13px] font-semibold text-ipc-light hover:underline">
          {allOn ? 'Turn all off' : 'Turn all on'}
          <span className="sr-only"> in {categoryTitle(cat)}</span>
        </button>
      </div>
      <p className="mt-0.5 text-[13px] text-ipc-muted">{cat.description}</p>
      <ul className="mt-2">
        {cat.collectors.map((c) => (
          <SourceRow key={c.id} c={c} form={form} isNew={newIds.has(c.id)} referenceHref={referenceHref} waitsForConsent={needsConsent.has(c.id)} />
        ))}
      </ul>
    </div>
  );
}

/**
 * The same two choices as onboarding (product owner decision 2026-10-22): the Default set, or a custom choice with Select
 * all. Nothing is saved until Save changes; new permissions then wait for the admin consent (Needs admin consent).
 */
export function SelectionShortcuts({ form }) {
  const isDefault = form.enabled.size === ESSENTIAL_SOURCES.length && ESSENTIAL_SOURCES.every((id) => form.enabled.has(id));
  const allOn = form.enabled.size >= ALL_COLLECTOR_IDS.length;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px]" data-testid="selection-shortcuts">
      <span className="text-ipc-muted" data-selection={isDefault ? 'default' : 'custom'}>
        {isDefault ? 'Using the Default set (recommended).' : 'Custom selection.'}
      </span>
      {!isDefault ? (
        <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => form.setSources(ESSENTIAL_SOURCES)} disabled={!form.loaded}>
          Use the Default set
        </button>
      ) : null}
      {!allOn ? (
        <button type="button" className="tap font-semibold text-ipc-light hover:underline" onClick={() => form.setSources(ALL_COLLECTOR_IDS)} disabled={!form.loaded}>
          Select all
        </button>
      ) : null}
    </div>
  );
}

const MSG_TONE = Object.freeze({ ok: 'good', warn: 'warn', err: 'bad' });

/** A save outcome line: a left rule in the status color and ink text. */
export function SaveMessage({ msg }) {
  if (!msg) return null;
  const tone = MSG_TONE[msg.kind] ?? 'bad';
  return (
    <div
      role={msg.kind === 'err' ? 'alert' : 'status'}
      className="mt-3 rounded-xl border border-ipc-border bg-white px-4 py-3 text-sm text-ipc-ink"
      style={{ borderLeft: `4px solid var(--${tone})` }}
      data-save-message={msg.kind}
    >
      {msg.text}
    </div>
  );
}

/**
 * @param {{ customerId: string, form: ReturnType<typeof import('./use-collection-settings.js').useCollectionSettings>,
 *   unavailable?: Array<{ id: string, label: string, reason: string }>, needsConsent?: Set<string> }} props
 * `needsConsent`: the sources waiting for an admin consent to new permissions (ENTRAID-350), marked per row.
 */
export default function SourcesCard({ customerId, form, unavailable = [], needsConsent = new Set() }) {
  const newIds = new Set(form.newSources.map((s) => s.id));
  const referenceHref = (key) => `${customerSectionPath(customerId, 'data-reference')}#source-${key}`;
  const sticky = form.sourcesDirty;
  return (
    <div id={SOURCES_ID} tabIndex={-1} className="scroll-mt-6 focus:outline-none">
      <SectionCard
        title="Sources"
        description="Choose what to collect from Microsoft Entra ID and Microsoft 365. Changes apply from the next collection."
        action={<span className="font-medium text-ipc-muted" data-testid="sources-count">{form.enabled.size} of {ALL_COLLECTOR_IDS.length} on</span>}
        testId="sources-card"
      >
        {unavailable.length > 0 ? (
          <div className="mb-4" data-testid="sources-unavailable">
            <h3>Need a license or a permission</h3>
            <ul className="mt-1 space-y-1">
              {unavailable.map((s) => (
                <li key={s.id} className="flex gap-2 text-[13px] text-ipc-ink">
                  <Dot color="var(--warn-dot)" />
                  <span><span className="font-semibold">{s.label}</span>: {s.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <SelectionShortcuts form={form} />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {COLLECTOR_CATEGORIES.map((cat) => <Category key={cat.id} cat={cat} form={form} newIds={newIds} referenceHref={referenceHref} needsConsent={needsConsent} />)}
        </div>
        <PermissionsPanel enabled={form.enabled} />
        <div
          className={`mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-ipc-border bg-white pt-3 ${sticky ? 'sticky bottom-0 z-10 -mx-6 -mb-6 rounded-b-2xl px-6 pb-4 shadow-[0_-1px_0_var(--line)]' : ''}`}
          data-testid="sources-save-bar"
          data-sticky={sticky ? 'true' : 'false'}
        >
          <span className="text-[13px] text-ipc-muted">
            {sticky ? 'You have unsaved changes to the sources.' : 'Changes take effect on the next scheduled collection.'}
          </span>
          <span className="flex items-center gap-2">
            {sticky ? <button type="button" className="btn-secondary text-sm" onClick={form.revertSources} disabled={form.busy}>Discard</button> : null}
            <button type="button" className="btn-primary text-sm" disabled={form.busy} onClick={() => form.save('datasources')}>
              {form.busy ? 'Saving...' : 'Save changes'}
            </button>
          </span>
        </div>
        {form.msgSource === 'datasources' ? <SaveMessage msg={form.msg} /> : null}
      </SectionCard>
    </div>
  );
}
